import React, { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import TID from "@/lib/testIds";
import { AppLogo } from "@/components/app/Primitives";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const COUNTRIES = ["India", "United States", "United Kingdom", "Singapore", "Australia", "Japan", "UAE"];

export default function InfluencerSignup() {
  const { otpRequest, otpVerify } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [f, setF] = useState({
    legal_name: "", email: "", mobile: "", country: "India",
    influencer_type: "individual", company_name: "",
  });
  const [busy, setBusy] = useState(false);
  const [mockCodes, setMockCodes] = useState(null);
  const [emailOtp, setEmailOtp] = useState("");
  const [phoneOtp, setPhoneOtp] = useState("");

  // Prefill email/mobile when redirected from /login → OTP tab.
  useEffect(() => {
    const em = searchParams.get("email");
    const mob = searchParams.get("mobile");
    if (em || mob) {
      setF((cur) => ({ ...cur, email: em || cur.email, mobile: mob || cur.mobile }));
    }
  }, [searchParams]);

  const requestOtp = async (e) => {
    e.preventDefault();
    if (!f.legal_name || !f.email || !f.mobile) return toast.error("Please fill all required fields");
    setBusy(true);
    try {
      const data = await otpRequest(f.email, f.mobile);
      setMockCodes({ email: data.email_otp, phone: data.phone_otp });
      setStep(2);
      toast.success("OTP sent (mocked — codes shown below)");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await otpVerify({
        email: f.email, mobile: f.mobile,
        email_otp: emailOtp, phone_otp: phoneOtp,
        legal_name: f.legal_name, country: f.country,
        influencer_type: f.influencer_type, company_name: f.company_name || undefined,
      });
      toast.success("Welcome to the ProPay Influencer Network");
      nav("/influencer/kys");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verify failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/"><AppLogo /></Link>
          <Link to="/login" className="text-sm text-slate-600 hover:text-slate-900">Already have an account? Sign in</Link>
        </div>
      </div>
      <div className="max-w-2xl mx-auto px-6 py-14">
        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#0052FF]">Influencer Registration · Step {step} of 2</div>
        <h1 className="font-heading text-4xl font-black tracking-tight text-slate-900 mt-2">Join the ProPay Influencer Network</h1>
        <p className="text-slate-600 mt-2">Secure registration via email + phone OTP. No password to remember.</p>

        {step === 1 ? (
          <form onSubmit={requestOtp} className="mt-10 bg-white rounded-2xl border border-slate-200 p-8 space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Type</label>
                <Select value={f.influencer_type} onValueChange={(v) => setF({ ...f, influencer_type: v })}>
                  <SelectTrigger data-testid={TID.signupType} className="h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="individual">Individual Influencer</SelectItem>
                    <SelectItem value="business">Registered Company / LLP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Country</label>
                <Select value={f.country} onValueChange={(v) => setF({ ...f, country: v })}>
                  <SelectTrigger data-testid={TID.signupCountry} className="h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>{COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Legal name *</label>
                <Input data-testid={TID.signupLegalName} required value={f.legal_name} onChange={(e) => setF({ ...f, legal_name: e.target.value })} className="h-11" placeholder="As per government ID" />
              </div>
              {f.influencer_type === "business" && (
                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Company / Trade name</label>
                  <Input data-testid={TID.signupCompany} value={f.company_name} onChange={(e) => setF({ ...f, company_name: e.target.value })} className="h-11" placeholder="Studio Creators LLP" />
                </div>
              )}
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Email *</label>
                <Input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="h-11" placeholder="you@example.com" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Mobile *</label>
                <Input required value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} className="h-11" placeholder="+91-9876543210" />
              </div>
            </div>
            <Button data-testid={TID.signupSubmit} type="submit" disabled={busy} className="w-full h-11 bg-[#0052FF] hover:bg-[#0043D9]">
              {busy ? "Sending OTP…" : "Send OTP → Verify → Continue"}
            </Button>
            <p className="text-xs text-slate-500 text-center">By continuing you agree to PROPAY GLOBAL PRIVATE LIMITED's terms. KYS verification will follow via Cashfree Secure ID.</p>
          </form>
        ) : (
          <form onSubmit={verify} className="mt-10 bg-white rounded-2xl border border-slate-200 p-8 space-y-5">
            {mockCodes && (
              <div className="text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg p-3">
                <div className="font-bold uppercase tracking-widest mb-1">Demo mode — codes returned in response</div>
                Email OTP: <code className="font-mono font-bold">{mockCodes.email}</code> · Phone OTP: <code className="font-mono font-bold">{mockCodes.phone}</code>
                <button type="button" className="ml-2 underline" onClick={() => { setEmailOtp(mockCodes.email); setPhoneOtp(mockCodes.phone); }}>Auto-fill</button>
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Email OTP (sent to {f.email})</label>
                <Input value={emailOtp} onChange={(e) => setEmailOtp(e.target.value)} maxLength={6} className="h-11 tracking-widest font-mono" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Phone OTP (sent to {f.mobile})</label>
                <Input value={phoneOtp} onChange={(e) => setPhoneOtp(e.target.value)} maxLength={6} className="h-11 tracking-widest font-mono" />
              </div>
            </div>
            <Button type="submit" disabled={busy} className="w-full h-11 bg-[#0052FF] hover:bg-[#0043D9]">
              {busy ? "Verifying…" : "Verify & Create Account"}
            </Button>
            <button type="button" onClick={() => setStep(1)} className="text-xs text-slate-500 hover:text-slate-900 underline w-full">← Edit details</button>
          </form>
        )}
      </div>
    </div>
  );
}
